import { TAO_PROJECT_TAB_LABELS } from "@hex/common";
import React from "react";

import {
  AppViewIcon,
  FilesIcon,
  NewThreadIcon,
  NotebookViewIcon,
} from "../icons/CustomIcons";

/** Stable view ids for plain-code project shell tabs (do not rename). */
export const TAO_PROJECT_VIEW = {
  chat: "chat",
  code: "code",
  files: "files",
  preview: "preview",
  split: "split",
} as const;

export type TaoProjectView =
  (typeof TAO_PROJECT_VIEW)[keyof typeof TAO_PROJECT_VIEW];

export function isTaoProjectViewTabSelected(
  selectedView: TaoProjectView,
  tab: TaoProjectView,
): boolean {
  return (
    selectedView === tab ||
    (selectedView === TAO_PROJECT_VIEW.split &&
      (tab === TAO_PROJECT_VIEW.code || tab === TAO_PROJECT_VIEW.preview))
  );
}

export function resolveAvailableTaoProjectView(
  requestedView: TaoProjectView,
  availableViews: ReadonlyArray<TaoProjectViewDefinition>,
): TaoProjectView {
  const isAvailable = (view: TaoProjectView): boolean =>
    availableViews.some(({ id }) => id === view);
  if (requestedView === TAO_PROJECT_VIEW.split) {
    return isAvailable(TAO_PROJECT_VIEW.code)
      ? TAO_PROJECT_VIEW.split
      : isAvailable(TAO_PROJECT_VIEW.preview)
        ? TAO_PROJECT_VIEW.preview
        : TAO_PROJECT_VIEW.chat;
  }
  return isAvailable(requestedView) ? requestedView : TAO_PROJECT_VIEW.chat;
}

export type TaoProjectViewDefinition = {
  readonly id: TaoProjectView;
  readonly label: string;
  readonly icon: React.ReactElement;
};

export const TAO_PROJECT_VIEW_DEFINITIONS: ReadonlyArray<TaoProjectViewDefinition> =
  [
    {
      id: TAO_PROJECT_VIEW.chat,
      label: TAO_PROJECT_TAB_LABELS.chat,
      icon: <NewThreadIcon />,
    },
    {
      id: TAO_PROJECT_VIEW.preview,
      label: TAO_PROJECT_TAB_LABELS.preview,
      icon: <AppViewIcon />,
    },
    {
      id: TAO_PROJECT_VIEW.code,
      label: TAO_PROJECT_TAB_LABELS.code,
      icon: <NotebookViewIcon />,
    },
    {
      id: TAO_PROJECT_VIEW.files,
      label: TAO_PROJECT_TAB_LABELS.files,
      icon: <FilesIcon />,
    },
  ];

/**
 * Explorer seats never author code by hand, so they lose Notebook and Files, and
 * lose App too when the workspace does not let Explorers create apps.
 * Member seats only reach the source on unlisted thread projects (listed ones
 * redirect them to the app), and there they get Agent alone.
 */
export function restrictTaoProjectViewsForSeat({
  availableViews,
  explorersCanCreateApps,
  isExplorer,
  isMember,
}: {
  availableViews: ReadonlyArray<TaoProjectViewDefinition>;
  explorersCanCreateApps: boolean;
  isExplorer: boolean;
  isMember: boolean;
}): ReadonlyArray<TaoProjectViewDefinition> {
  if (isMember) {
    return availableViews.filter((view) => view.id === TAO_PROJECT_VIEW.chat);
  }
  if (!isExplorer) {
    return availableViews;
  }
  return availableViews.filter(
    (view) =>
      view.id !== TAO_PROJECT_VIEW.code &&
      view.id !== TAO_PROJECT_VIEW.files &&
      (explorersCanCreateApps || view.id !== TAO_PROJECT_VIEW.preview),
  );
}

/**
 * Inspect opens a cell and its upstream from an app output, so it comes with
 * App rather than Notebook. Explorer seats can inspect although they never
 * author code by hand (cell editing stays closed to them separately); app-only
 * Member seats, who never see project contents, cannot.
 */
export function canInspectTaoProjectCells(
  availableViews: ReadonlyArray<TaoProjectViewDefinition>,
): boolean {
  return availableViews.some(({ id }) => id === TAO_PROJECT_VIEW.preview);
}

/** Max content width for the Chat tab agent UI (matches logic-view fullscreen threads). */
export const TAO_PROJECT_FULLSCREEN_CHAT_CONTENT_MAX_WIDTH = 760;
/** Narrowest the chat gets beside a side panel (the thread inspector, the project container) before the panel hides. */
export const TAO_PROJECT_CHAT_BESIDE_PANEL_MIN_WIDTH_PX = 480;
